import React, { useState, createElement } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { History, Search, Filter, Download, FileText, Shield, User, Activity, Settings, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { useStore } from '../data/store';
import { EmptyState } from '../components/EmptyState';
import { FilterBar, FilterField } from '../components/FilterBar';
import { toast } from 'sonner';
import { SyncButton } from '../components/SyncButton';
const KATEGORI_LOG = ['Aktivitas Login', 'Aktivitas Pengguna', 'Perubahan Laporan', 'Aktivitas Verifikasi', 'Tindakan Administratif', 'Manajemen Pengguna', 'Ekspor Data', 'Perubahan Sistem'];
export default function AuditLog() {
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const auditLogList = useStore(s => s.auditLog);
  const filteredLogs = auditLogList.filter(log => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || log.actor.toLowerCase().includes(search) || log.action.toLowerCase().includes(search) || log.detail.toLowerCase().includes(search) || log.id.toLowerCase().includes(search) || log.ipAddress.toLowerCase().includes(search);
    const matchesCategory = !filterValues.category || filterValues.category === 'all' || log.category === filterValues.category;
    const matchesRole = !filterValues.role || filterValues.role === 'all' || log.role === filterValues.role;
    const matchesResult = !filterValues.result || filterValues.result === 'all' || log.result === filterValues.result;
    return matchesSearch && matchesCategory && matchesRole && matchesResult;
  });
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari pengguna, aktivitas, IP...'
  }, {
    key: 'category',
    label: 'Modul',
    type: 'select',
    options: KATEGORI_LOG.map(c => ({
      label: c,
      value: c
    }))
  }, {
    key: 'role',
    label: 'Role',
    type: 'select',
    options: [{
      label: 'Super Admin Nasional',
      value: 'Super Admin Nasional'
    }, {
      label: 'Admin BINDA',
      value: 'Admin BINDA'
    }, {
      label: 'Analis',
      value: 'Analis'
    }, {
      label: 'Operator Command Center',
      value: 'Operator Command Center'
    }]
  }, {
    key: 'result',
    label: 'Status Aksi',
    type: 'select',
    options: [{
      label: 'Berhasil',
      value: 'Berhasil'
    }, {
      label: 'Gagal',
      value: 'Gagal'
    }, {
      label: 'Peringatan',
      value: 'Peringatan'
    }]
  }, {
    key: 'date',
    label: 'Tanggal',
    type: 'datePreset'
  }];
  const exportColumns = [{
    key: 'id',
    label: 'ID Log'
  }, {
    key: 'timestamp',
    label: 'Waktu'
  }, {
    key: 'actor',
    label: 'Aktor'
  }, {
    key: 'role',
    label: 'Peran'
  }, {
    key: 'category',
    label: 'Kategori'
  }, {
    key: 'action',
    label: 'Aksi'
  }, {
    key: 'detail',
    label: 'Detail'
  }, {
    key: 'target',
    label: 'Target'
  }, {
    key: 'ipAddress',
    label: 'Alamat IP'
  }, {
    key: 'result',
    label: 'Hasil'
  }];
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Aktivitas Login':
        return <Shield className="w-3 h-3" data-id="element-807" />;
      case 'Aktivitas Pengguna':
        return <User className="w-3 h-3" data-id="element-808" />;
      case 'Perubahan Laporan':
        return <FileText className="w-3 h-3" data-id="element-809" />;
      case 'Aktivitas Verifikasi':
        return <Activity className="w-3 h-3" data-id="element-810" />;
      case 'Tindakan Administratif':
        return <Settings className="w-3 h-3" data-id="element-811" />;
      default:
        return <History className="w-3 h-3" data-id="element-812" />;
    }
  };
  const getResultBadge = (result: string) => {
    switch (result) {
      case 'Berhasil':
        return <Badge variant="outline" className="text-[9px] font-mono border-[var(--status-active)]/50 text-[var(--status-active)] bg-[var(--status-active)]/10" data-id="element-813">
            <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-814" /> BERHASIL
          </Badge>;
      case 'Gagal':
        return <Badge variant="outline" className="text-[9px] font-mono border-destructive/50 text-destructive bg-destructive/10" data-id="element-815">
            <XCircle className="w-3 h-3 mr-1" data-id="element-816" /> GAGAL
          </Badge>;
      case 'Peringatan':
        return <Badge variant="outline" className="text-[9px] font-mono border-[var(--status-standby)]/50 text-[var(--status-standby)] bg-[var(--status-standby)]/10" data-id="element-817">
            <AlertTriangle className="w-3 h-3 mr-1" data-id="element-818" /> PERINGATAN
          </Badge>;
      default:
        return <Badge variant="outline" className="text-[9px] font-mono" data-id="element-819">
            {result}
          </Badge>;
    }
  };
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-820">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-821">
        <div data-id="element-822">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-823">
            DENS CAKRA AUDIT CENTER
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-824">
            System Activity &amp; Compliance Monitoring
          </p>
        </div>
        <div className="flex gap-3" data-id="element-825">
          <SyncButton data-id="element-826" />
        </div>
      </div>

      <Card className="glass-panel-heavy border-border/50 shadow-[0_0_20px_rgba(0,0,0,0.5)]" data-id="element-827">
        <CardHeader className="border-b border-border/50 bg-card/50 flex flex-col md:flex-row md:items-center justify-between gap-4 p-4" data-id="element-828">
          <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2 shrink-0" data-id="element-829">
            <History className="w-4 h-4 text-primary" data-id="element-830" /> Rekaman Aktivitas
          </CardTitle>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" data-id="element-831">
            <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-832">
              <Filter className="w-4 h-4 mr-2" data-id="element-833" />
              Filter Log
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-id="element-834">
          {showFilters && <div className="p-4 border-b border-border/50 bg-black/20" data-id="element-835">
              <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredLogs.length} exportData={filteredLogs} exportColumns={exportColumns} exportFilename="Log_Audit_DENS_CAKRA" data-id="element-836" />
            </div>}
          {filteredLogs.length === 0 ? <EmptyState icon={Search} title="TIDAK ADA REKAMAN" description="Tidak ada log audit yang sesuai dengan kriteria pencarian atau filter saat ini." actionLabel="Bersihkan Filter" onAction={handleResetFilters} className="border-0 rounded-none rounded-b-lg" data-id="element-837" /> : <Table data-id="element-838">
              <TableHeader className="bg-black/20" data-id="element-839">
                <TableRow className="border-border/30 hover:bg-transparent" data-id="element-840">
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground w-[160px]" data-id="element-841">
                    Waktu & ID
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground w-[180px]" data-id="element-842">
                    Aktor & Peran
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground w-[160px]" data-id="element-843">
                    Kategori
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-844">
                    Aksi & Detail
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground w-[120px]" data-id="element-845">
                    Target / IP
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right w-[100px]" data-id="element-846">
                    Hasil
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-847">
                {filteredLogs.map(log => <TableRow key={log.id} className="border-border/20 hover:bg-white/5 transition-colors" data-id="element-848">
                    <TableCell data-id="element-849">
                      <div className="font-mono text-xs text-foreground/90" data-id="element-850">
                        {log.timestamp}
                      </div>
                      <div className="font-mono text-[9px] text-muted-foreground mt-0.5" data-id="element-851">
                        {log.id}
                      </div>
                    </TableCell>
                    <TableCell data-id="element-852">
                      <div className="text-xs font-medium text-primary" data-id="element-853">
                        {log.actor}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5" data-id="element-854">
                        {log.role}
                      </div>
                    </TableCell>
                    <TableCell data-id="element-855">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-856">
                        {getCategoryIcon(log.category)}
                        <span data-id="element-857">{log.category}</span>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-858">
                      <div className="text-xs font-bold text-foreground/90" data-id="element-859">
                        {log.action}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug" data-id="element-860">
                        {log.detail}
                      </div>
                    </TableCell>
                    <TableCell data-id="element-861">
                      <div className="font-mono text-[10px] text-foreground/80" data-id="element-862">
                        {log.target}
                      </div>
                      <div className="font-mono text-[9px] text-muted-foreground mt-0.5" data-id="element-863">
                        {log.ipAddress}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" data-id="element-864">
                      {getResultBadge(log.result)}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>}
        </CardContent>
      </Card>
    </div>;
}